// SignIn.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    TextField,
    Button,
    Checkbox,
    FormControlLabel,
    Alert,
    Container,
    Grid,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    RecaptchaVerifier,
    signInWithPhoneNumber,
    setPersistence,
    browserLocalPersistence,
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebaseConfig';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import ModernMap from './ModernMap';
import AuthCard from './AuthCard'; // Import the new AuthCard component

const SignIn = () => {
    const [phone, setPhone] = useState('');
    const [otp, setOtp] = useState('');
    const [isValet, setIsValet] = useState(false);
    const [confirmationResult, setConfirmationResult] = useState(null);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const controls = useAnimation();
    const [ref, inView] = useInView();

    useEffect(() => {
        if (inView) {
            controls.start('visible');
        }
    }, [controls, inView]);

    useEffect(() => {
        const setupRecaptcha = async () => {
            try {
                await setPersistence(auth, browserLocalPersistence);

                if (window.recaptchaVerifier) {
                    window.recaptchaVerifier.clear();
                }

                window.recaptchaVerifier = new RecaptchaVerifier(
                    auth,
                    'sign-in-button',
                    {
                        size: 'invisible',
                        callback: () => console.log('reCAPTCHA verified'),
                        'expired-callback': () => {
                            setError('reCAPTCHA expired. Please try again.');
                            window.recaptchaVerifier.clear();
                            window.recaptchaVerifier = null;
                        },
                    }
                );

                await window.recaptchaVerifier.render();
            } catch (error) {
                console.error('Error setting up reCAPTCHA:', error);
                setError(
                    'Failed to initialize sign-in. Please refresh the page.'
                );
            }
        };

        setupRecaptcha();

        return () => {
            if (window.recaptchaVerifier) {
                window.recaptchaVerifier.clear();
                window.recaptchaVerifier = null;
            }
        };
    }, []);

    const handleSignIn = async () => {
        if (phone.length !== 10) {
            setError('Please enter a valid 10-digit phone number.');
            return;
        }

        setError(null);

        try {
            if (!window.recaptchaVerifier) {
                throw new Error('reCAPTCHA not initialized');
            }

            const phoneNumber = '+1' + phone;
            const appVerifier = window.recaptchaVerifier;

            const confirmation = await signInWithPhoneNumber(
                auth,
                phoneNumber,
                appVerifier
            );
            setConfirmationResult(confirmation);
        } catch (error) {
            console.error('Error during sign-in:', error);
            setError(error.message || 'Failed to send OTP. Please try again.');

            if (window.recaptchaVerifier) {
                window.recaptchaVerifier.clear();
                window.recaptchaVerifier = null;
            }

            try {
                window.recaptchaVerifier = new RecaptchaVerifier(
                    auth,
                    'sign-in-button',
                    {
                        size: 'invisible',
                        callback: () => console.log('reCAPTCHA verified'),
                        'expired-callback': () =>
                            console.log('reCAPTCHA expired'),
                    }
                );
                await window.recaptchaVerifier.render();
            } catch (renderError) {
                console.error('Error reinitializing reCAPTCHA:', renderError);
                setError('Please refresh the page and try again.');
            }
        }
    };

    const verifyOtp = async () => {
        if (!otp || otp.length !== 6) {
            setError('Please enter a valid 6-digit OTP.');
            return;
        }

        setError(null);

        try {
            const result = await confirmationResult.confirm(otp);
            const user = result.user;

            const userDocRef = doc(db, 'users', user.uid);
            const userDoc = await getDoc(userDocRef);

            console.log(userDoc.data());

            if (!userDoc.exists()) {
                await setDoc(
                    userDocRef,
                    {
                        uid: user.uid,
                        phoneNumber: user.phoneNumber,
                        role: isValet ? 'valet' : 'customer',
                        createdAt: new Date(),
                        status: 'active',
                    },
                    { merge: true }
                );
            }

            navigate('/home');
        } catch (error) {
            console.error('Error verifying OTP:', error);
            setError('Invalid OTP. Please try again.');
        }
    };

    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100vh',
                justifyContent: 'center', // Center vertically
                alignItems: 'center', // Center horizontally
                padding: 0,
                margin: 0,
            }}>
            <ModernMap />

            <Container
                maxWidth='sm'
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center', // Center horizontally
                    padding: theme.spacing(2),
                    position: 'relative',
                    zIndex: 1,
                    width: '100%', // Ensure the container takes full width
                }}>
                <motion.div
                    ref={ref}
                    initial='hidden'
                    animate={controls}
                    variants={containerVariants}>
                    <AuthCard
                        title='Welcome to Valet Network'
                        subtitle='A valet at your service in seconds'>
                        {error && (
                            <Alert severity='error' sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    label='Phone Number'
                                    fullWidth
                                    value={phone}
                                    onChange={(e) =>
                                        setPhone(
                                            e.target.value
                                                .replace(/\D/g, '')
                                                .slice(0, 10)
                                        )
                                    }
                                    placeholder='Enter 10-digit phone number'
                                    sx={{ mb: 2 }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isValet}
                                            onChange={(e) =>
                                                setIsValet(e.target.checked)
                                            }
                                        />
                                    }
                                    label='I am a Valet'
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <div id='sign-in-button'></div>
                                <Button
                                    variant='contained'
                                    fullWidth
                                    onClick={handleSignIn}
                                    disabled={phone.length !== 10}
                                    sx={{
                                        mb: 2,
                                        backgroundColor: '#113524',
                                        '&:hover': {
                                            backgroundColor: '#1E4A3E',
                                        },
                                        fontSize: isMobile
                                            ? '0.875rem'
                                            : '1rem',
                                        fontWeight: 500,
                                        textTransform: 'none',
                                        padding: theme.spacing(1.5),
                                    }}>
                                    Send OTP
                                </Button>
                            </Grid>
                            {confirmationResult && (
                                <>
                                    <Grid item xs={12}>
                                        <TextField
                                            label='Enter OTP'
                                            fullWidth
                                            value={otp}
                                            onChange={(e) =>
                                                setOtp(
                                                    e.target.value
                                                        .replace(/\D/g, '')
                                                        .slice(0, 6)
                                                )
                                            }
                                            sx={{ mb: 2 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button
                                            variant='contained'
                                            fullWidth
                                            onClick={verifyOtp}
                                            sx={{
                                                backgroundColor: '#113524',
                                                '&:hover': {
                                                    backgroundColor: '#1E4A3E',
                                                },
                                                fontSize: isMobile
                                                    ? '0.875rem'
                                                    : '1rem',
                                                fontWeight: 500,
                                                textTransform: 'none',
                                                padding: theme.spacing(1.5),
                                            }}>
                                            Verify OTP
                                        </Button>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </AuthCard>
                </motion.div>
            </Container>
        </Box>
    );
};

export default SignIn;
